<template>
    <vue-custom-scrollbar
        ref="scrollContainer"
        tagname="div"
        :settings="scrollSettings"
        style="height: 100%;"
    >
        <div class="toolbar-editor__options" @click="onAnyClick">
            <template v-if="selectedElements.length">
                <div class="toolbar-editor__switch">
                    <h4 class="toolbar-editor__switch-title">
                        {{ displayName }}
                    </h4>

                    <i class="toolbar-editor__switch-icon" @click.prevent="selectParentElement()">
                        <font-awesome-icon :icon="['fal', 'level-up']" />
                    </i>
                </div>

                <accordion v-if="hasOptions" :data="getAccordionData()" :visible="visibleOption">
                    <template v-slot="{ data }">
                        <ul class="options">
                            <li v-for="option in data.options" :key="option.component" class="options__item">
                                <component :is="option.component" />
                            </li>
                        </ul>
                    </template>
                </accordion>

                <div v-if="!isLockedComponent && !hasOptions" class="toolbar-editor__empty">
                    There are no options available for the currently selected element(s). <br> <br>

                    Please try selecting a different element.
                </div>

                <div v-if="isLockedComponent && !isGlobalContent" class="toolbar-editor__empty">
                    This component is locked, so styles can only be modified from within the Component Editor. <br> <br>
                    <div v-if="user.designer">
                        Alternatively, you may choose to unlock the component. <br> <br>

                        <a
                            :href="`/components/${ selectedRendering.linkedComponentNew }`"
                            class="toolbar-editor__button toolbar-editor__button--modify"
                        >
                            <font-awesome-icon
                                class="toolbar-editor__button-icon"
                                :icon="['fal', 'edit']"
                            />

                            Modify Component
                        </a>

                        <button
                            class="toolbar-editor__button toolbar-editor__button--unlock"
                            type="button"
                            @click="onUnlockComponent"
                        >
                            <font-awesome-icon
                                class="toolbar-editor__button-icon"
                                :icon="['fal', 'lock-alt']"
                            />

                            Unlock Component
                        </button>
                    </div>
                </div>

                <div v-if="isGlobalContent && !isComponentScreen" class="toolbar-editor__empty">
                    This is a Global Content, so the content and styles can only be modified from within the Component Editor. <br> <br>
                    <div v-if="user.designer">
                        <a
                            :href="`/components/${ selectedRendering.linkedComponentNew }`"
                            class="toolbar-editor__button toolbar-editor__button--modify"
                        >
                            <font-awesome-icon
                                class="toolbar-editor__button-icon"
                                :icon="['fal', 'edit']"
                            />

                            Modify Component
                        </a>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="toolbar-editor__empty">
                    Please select an element.
                </div>
            </template>
        </div>
    </vue-custom-scrollbar>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';

import Accordion from '@/components/utilities/accordion';
import { AccordionItem } from '@/data/data-models';
import { SELECT_PARENT_ELEMENT } from '@/store/types/mutation-types';
import optionTypes from '@/options/definition/option-types';
import optionTargets from '@/options/definition/option-targets';
import { objectKeyIntersection } from '@/utils/object';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { SCREEN_NAMES } from '@/data/constants/screen';
import mapFilters from '@/utils/map-filters';

export default {
    name: 'toolbar-options',
    components: { Accordion, vueCustomScrollbar },
    mixins: [listenOnRootMixin],

    props: {
        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            isLocking: false,

            scrollSettings: {
                suppressScrollX: true,
            },

            visibleOption: '',
        };
    },

    computed: {
        ...mapState({
            deviceMode: state => state.page.deviceMode,
            selectedElements: state => state.page.selectedElements,
            selectedRendering: state => state.page.selectedRendering,
            user: state => state.user.user,
        }),

        elementNames() {
            if (!this.selectedElements.length) return [];

            const set = new Set(this.selectedElements.map(element => element.$options._componentTag || element.$el.tagName));

            return Array.from(set);
        },

        displayName() {
            if (this.selectedElements.length > 1) return 'Multiple elements selected';

            return this.selectedElements[0].getElementData('displayName') || this.selectedElements[0].displayName;
        },

        hasOptions() {
            return !!Object.keys(this.getAccordionData()).length;
        },

        isComponentScreen() {
            return this.screen === SCREEN_NAMES.COMPONENT_EDITOR;
        },

        isLockedComponent() {
            if (!this.selectedRendering || this.isTemplateSelected) return false;

            return this.selectedRendering.lockComponentNew && !this.isComponentScreen;
        },

        isGlobalContent() {
            if (!this.selectedRendering || this.isTemplateSelected) return false;

            return this.selectedRendering.globalContent;
        },

        isTemplateSelected() {
            return this.selectedElements[0].atomicType === 'template'
                || this.selectedElements[0].$options.name === 'drop-zone-multi';
        },
    },

    watch: {
        selectedElements(newVal) {
            if (newVal && newVal.length) {
                const { elementType } = newVal[0];

                this.showOptionsOnElementSelect(elementType, 'a-image', 'image');
                this.showOptionsOnElementSelect(elementType, 'a-link', 'link-builder');
            }
        },
    },

    beforeMount() {
        this.listenOnRoot('flex::option::is-link', this.$forceUpdate);
    },

    mounted() {
        this.listenOnRoot('flex::accordion::expanded', this.scrollUp);
    },

    methods: {
        ...mapFilters(['paramCase']),

        ...mapMutations({
            selectParentElement: SELECT_PARENT_ELEMENT,
        }),

        getIcon(optionType) {
            return this.optionMetadata(optionType).icon;
        },

        getIconSet(optionType) {
            return this.optionMetadata(optionType).iconSet || 'fal';
        },

        getOptions(elementType) {
            const elementOptions = optionTargets[elementType];

            if (!elementOptions) console.warn('Unable to find any option mappings for', elementType);

            return { ...elementOptions } || {};
        },

        optionMetadata(optionType) {
            return Object.prototype.hasOwnProperty.call(optionTypes, optionType)
                ? optionTypes[optionType].metadata
                : {};
        },

        getAccordionData() {
            if (this.isGlobalContent && !this.isComponentScreen) {
                return [];
            }

            // first we need to gather data about the options that are shared across each of the currently selected elements
            const optionsData = this.elementNames.reduce((filteredOptions, elementName, index) => {
                // we do this by looping through this.elementNames, which contains the names of one or more selected elements
                // e.g. => ['a-heading', 'a-link']
                const newSharedOptions = {};

                // then we fetch the options object for the element in the current loop
                // e.g. => {
                //     layout: [layout.height, layout.width],
                //     component: [component.border, component.margin, component.padding],
                // }
                const elementOptions = this.getOptions(elementName);

                if (index === 0) return this.removeSingletonOptions(elementOptions);

                // next, we get the shared option types for the element in the current loop
                // ... and the shared option types found during previous loops
                // e.g. => ['layout', 'component', 'text']
                const sharedTypes = objectKeyIntersection(filteredOptions, elementOptions);

                // then we fetch an array of shared options for each type
                // e.g. => [layout.height, layout.width]
                sharedTypes.forEach((prop) => {
                    newSharedOptions[prop] = this.optionsArrayIntersection(filteredOptions[prop], elementOptions[prop]);
                });

                return this.removeSingletonOptions(newSharedOptions);
            }, {});

            // next we filter out options that shouldn't be displayed, and sort them
            Object.keys(optionsData).forEach((optionGroupName) => {
                optionsData[optionGroupName] = this.filterOptions(optionsData[optionGroupName]);
                optionsData[optionGroupName] = this.sortOptions(optionGroupName, optionsData[optionGroupName]);

                if (!optionsData[optionGroupName].length) {
                    delete optionsData[optionGroupName];
                }
            });

            // finally, mutate the optionsData into an accordion-ready format
            return Object.keys(optionsData).map((optionKey) => {
                return new AccordionItem({
                    id: this.paramCase(optionKey),
                    name: `${ optionTypes[optionKey].metadata.title } Options`,
                    icon: this.getIcon(optionKey),
                    iconSet: this.getIconSet(optionKey),
                    data: {
                        options: optionsData[optionKey],
                    },
                });
            });
        },

        // some options are only meant to be applied to one element at a time
        removeSingletonOptions(options) {
            // short circuit if only one element is selected
            if (this.selectedElements.length === 1) return options;

            const groupOptions = {};

            Object.keys(options).forEach((optionGroupName) => {
                groupOptions[optionGroupName] = groupOptions[optionGroupName] || [];

                options[optionGroupName].forEach((option) => {
                    if (option.metadata.groupOption) {
                        groupOptions[optionGroupName].push(option);
                    }
                });
            });

            return groupOptions;
        },

        scrollUp() {
            this.$refs.scrollContainer.$el.scrollTo(0, 0);
        },

        showOptionsOnElementSelect(currElementType, elementType, optionName) {
            if (currElementType === elementType) {
                this.visibleOption = optionName;
            }
        },

        filterOptions(options) {
            const filtered = [];

            options.forEach((option) => {
                const {
                    isResponsive,
                    lockedOption,
                    requiredProps,
                    disallowedProps,
                    screens,
                } = option.metadata;

                const hasRequiredProps = this.selectedElements.every((elem) => {
                    return requiredProps.some(requiredProp => elem[requiredProp]);
                });

                const hasDisallowedProps = this.selectedElements.every((elem) => {
                    return disallowedProps.some(disallowedProp => elem[disallowedProp]);
                });

                // don't show options that are missing a required property
                if (requiredProps.length && !hasRequiredProps) {
                    return;
                }

                // don't show options that include a disallowed property
                if (disallowedProps.length && hasDisallowedProps) {
                    return;
                }

                // don't show non-lock-enabled options for locked components
                if (this.isLockedComponent && !lockedOption) {
                    return;
                }

                // don't show options that are meant for other screens
                if (screens.length && !screens.includes(this.screen)) {
                    return;
                }

                // don't show non-responsive options, if we‘re not in desktop mode
                if (this.deviceMode.name !== 'all' && !isResponsive) {
                    return;
                }

                filtered.push(option);
            });

            return filtered;
        },

        sortOptions(optionTypeName, unsortedOptions) {
            const optionType = optionTypes[optionTypeName];
            const sortedOptions = [];

            // this sorts the options by the order they appear in option-types.js
            Object.values(optionType).forEach((option) => {
                if (!option.metadata) return;

                const matchingOption = unsortedOptions.find(unsortedOption => unsortedOption.metadata.name === option.metadata.name);

                if (matchingOption) {
                    sortedOptions.push(matchingOption);
                }
            });

            return sortedOptions;
        },

        /**
         * Finds the intersection (X ∩ Y) of two option-definition arrays.
         *
         * The array of options is the equal to the object found in `@/options/definition/options.js`; hence, each array
         * item has the following data-structure (with differing property values):
         *  {
         *       "metadata": {
         *           "contentOption": false,
         *           "groupOption": true,
         *           "requiredProp": "",
         *           "disallowedProp": "",
         *           "screens": []
         *       },
         *       "component": "align-self"
         *  }
         *
         * Args:
         *      arr1 (Array): Array of objects (data-structure above) of a given option type (e.g. an array for "text," "layout," etc. of options).
         *      arr2 (Array): Array of objects (data-structure above) of a given option type (e.g. an array for "text," "layout," etc. of options).
         *
         * Returns:
         *      (Array) The array of shared objects from the two option-definitions.
         */
        optionsArrayIntersection(arr1, arr2) {
            // create set of all object.component values for array:
            const optionsArr2 = new Set(arr2.map(obj => obj.component));

            return arr1.filter(obj => optionsArr2.has(obj.component));
        },

        onUnlockComponent() {
            this.isLocking = true;
            this.$emit('unlock');
        },

        onAnyClick() {
            this.$root.$emit('flex::dropdown::close');
        },
    },
};
</script>

<style lang="scss">
.options {
    margin: 0;
    padding: 0;

    &__item {
        position: relative;
        list-style: none;
        text-align: left;

        &:not(:last-of-type) {
            border-bottom: 1px solid var(--accordion-barBorderColor4);
        }
    }

    &__link {
        font-size: 12px;
        margin-top: 20px;
        display: block;
    }

    &__icon {
        font-size: 16px;
        position: relative;
        top: -1px;
        margin-left: 2px;
    }
}
</style>
